import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import { fr } from 'date-fns/locale/fr';
import useSearchWithParams from "../../hooks/useSearchWithParams";
import HelperMetiers360 from "../../services/HelpersMetiers360";
import DynamicTable from "../widgets/dynamicTable/DynamicTable";
import PropTypes from 'prop-types';
import { Col, Row } from "react-bootstrap";
import SelectMultipleItemsComponent from "../widgets/selectMultipleItemsComponent/SelectMultipleItemsComponent";
import { useStoreActions, useStoreState } from "easy-peasy";
import { Link } from "react-router-dom";

registerLocale('fr', fr);

const WebappAdminGlobalStatTableComponent = ({ tableCsv, clientsCategories }) => {

    const { fetchTagOperations } = useStoreActions(actions => actions.tagOperations);
    const { allTagOperations } = useStoreState(state => state.tagOperations);
    const clientSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('clients'));

    const [statsFiltered, setStatsFiltered] = useState(tableCsv);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedTagsOpe, setSelectedTagsOpe] = useState([]);
    const { searchInput, search } = useSearchWithParams({
        placeholder: 'Rechercher par établissement',
        delay: 400,
    });

    useEffect(() => {
        if (allTagOperations.length == 0) {
            fetchTagOperations();
        }
    }, []);

    useEffect(() => {
        if (tableCsv) {
            let newStats = [...tableCsv];
            if (search !== "") {
                newStats = newStats?.filter(stat => HelperMetiers360.isSearchInText(search, stat.client_name));
            }
            if (selectedCategories?.length) {
                newStats = newStats?.filter(stat => selectedCategories?.some(c => HelperMetiers360.isSearchInText(c.name, stat.category)))
            }
            if (selectedTagsOpe?.length) {
                newStats = newStats?.filter(stat => selectedTagsOpe?.some(c => HelperMetiers360.isSearchInText(c.name, stat.tags_operations)))
            }
            setStatsFiltered(newStats);
        }
    }, [search, selectedCategories, selectedTagsOpe])

    const globalStatsByClient = [
        {
            value: 'client_name',
            label: 'Établissement',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "client_name"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.client_name, b.client_name),
            display: (data) => {
                if (!data.client_name) {
                    return '-';
                }
                return clientSlugs?.readOne && data.client_unique_id ?
                    <Link to={clientSlugs.readOne.replace(':uniqueId', data.client_unique_id)}
                        onClick={(e) => e.stopPropagation()}
                        key={'client-' + data.client_unique_id}>
                        {data.client_name}</Link>
                    : <>{data.client_name}</>
            },
            flatDisplay: (data) => data.client_name ?? '-'
        },
        {
            value: 'category',
            label: 'Catégorie',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "category"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.category, b.category),
            display: (data) => data.category
        },
        {
            value: 'tags_operations',
            label: 'Opé M360',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "tags_operations"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.tags_operations, b.tags_operations),
            display: (data) => data.tags_operations
        },
        {
            value: 'user_count',
            label: 'Utilisateur⋅ices',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "user_count"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.user_count, b.user_count),
            display: (data) => data.user_count
        },
        {
            value: 'user_without_activity_count',
            label: 'Utilisateur⋅ices sans activité (en cours ou terminée)',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "user_without_activity_count"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.user_without_activity_count, b.user_without_activity_count),
            display: (data) => data.user_without_activity_count
        },
        {
            value: 'activity_syntheses_count',
            label: 'Activités terminées (synthèses produites)',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "activity_syntheses_count"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.activity_syntheses_count, b.activity_syntheses_count),
            display: (data) => data.activity_syntheses_count
        },
        {
            value: 'story_views',
            label: 'Vues stories',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "story_views"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.story_views, b.story_views),
            display: (data) => data.story_views
        },
        {
            value: 'video_views',
            label: 'XP vues via lecteur webapp',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "video_views"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.video_views, b.video_views),
            display: (data) => data.video_views
        },
        {
            value: 'video_views_from_activity',
            label: 'XP vues via lecteur webapp depuis une activité',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "video_views_from_activity"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.video_views_from_activity, b.video_views_from_activity),
            display: (data) => data.video_views_from_activity
        },
        {
            value: 'video_views_from_videos_menu',
            label: 'XP vues via lecteur webapp depuis le menu "Expériences"',
            test: HelperMetiers360.isArrayContainsValue(statsFiltered, "video_views_from_videos_menu"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.video_views_from_videos_menu, b.video_views_from_videos_menu),
            display: (data) => data.video_views_from_videos_menu
        },
    ]

    const categoryFilter = clientsCategories?.length > 0 && <div>
        <label className="label-select">Catégories&nbsp;: </label>
        <SelectMultipleItemsComponent itemsSelected={selectedCategories} setItemsSelected={setSelectedCategories}
            items={clientsCategories} name="contacts" itemKey="id" itemValue="name" id='select-category' />
    </div>;

    const tagsOpeFilter = allTagOperations?.length > 0 && <div>
        <label className="label-select">Opé&nbsp;M360&nbsp;: </label>
        <SelectMultipleItemsComponent itemsSelected={selectedTagsOpe} setItemsSelected={setSelectedTagsOpe}
            items={allTagOperations} name="opeM360" itemKey="id" itemValue="name" id='select-opeM360' />
    </div>

    return <>
        <Row>
            {searchInput}
        </Row>
        <Row>
            <Col>
                {categoryFilter}
            </Col>
            <Col>
                {tagsOpeFilter}
            </Col>
        </Row>
        <DynamicTable
            contentTable={statsFiltered}
            contentSort={globalStatsByClient}
            valueInitSort="user_count"
            index="client_unique_id"
            filename="statistiques_globales_par_établissement"
        />
    </>;
}

WebappAdminGlobalStatTableComponent.propTypes = {
    tableCsv: PropTypes.array.isRequired,
    clientsCategories: PropTypes.array.isRequired
};

export default WebappAdminGlobalStatTableComponent;